import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle, Send } from 'lucide-react';
import { businessAdminService, businessTicketService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/businessTheme.css';
import './BusinessTicketDetail.css';

const STATUSES = ['Open', 'In Progress', 'Redirected', 'Awaiting Supplier', 'Resolved', 'Closed'];

export default function BusinessTicketDetail() {
  const { id } = useParams();
  const { user, isBusinessLead } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [showNotify, setShowNotify] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await businessTicketService.getById(id);
    setTicket(res.data.ticket);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isBusinessLead) businessAdminService.listDepartments().then(res => setDepartments(res.data.departments.filter(d => d.isActive)));
  }, [isBusinessLead]);

  if (!ticket) return null;

  const patch = async (data) => {
    setError('');
    setBusy(true);
    try {
      const res = await businessTicketService.update(id, data);
      setTicket(res.data.ticket);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    await businessTicketService.addComment(id, { body: commentBody });
    setCommentBody('');
    load();
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyMessage.trim()) return;
    setError('');
    try {
      const res = await businessTicketService.notifySupplier(id, notifyMessage);
      setTicket(res.data.ticket);
      setNotifyMessage('');
      setShowNotify(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to notify supplier');
    }
  };

  const redirectLink = ticket.redirectToken ? `${window.location.origin}/business/redirect/${ticket.redirectToken}` : null;

  return (
    <div className="business-ticket-detail-page biz-theme biz-page">
      <Link to="/business/tickets" className="bt-back"><ArrowLeft size={12} /> Back to tickets</Link>

      <div className="page-header">
        <div>
          <h1 className="page-title">{ticket.ticketNumber} — {ticket.title}</h1>
          <p className="page-subtitle">
            <span className={`badge badge-${ticket.status.toLowerCase().replace(/\s+/g, '-')}`}>{ticket.status}</span>
            {' '}{ticket.plant?.name} · {ticket.category?.name} · {ticket.channel}
          </p>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="bt-detail-grid">
        <div className="bt-main">
          <div className="card">
            <div className="form-section-title">Description</div>
            <p className="bt-description">{ticket.description}</p>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div className="form-section-title">Comments</div>
            {ticket.comments.length === 0 && <p className="bt-empty">No comments yet.</p>}
            {ticket.comments.map((c, i) => (
              <div key={i} className="bt-comment">
                <div className="bt-comment-author">{c.author?.name || c.externalAuthorName || 'Unknown'}</div>
                <div className="bt-comment-body">{c.body}</div>
                <div className="bt-comment-time">{new Date(c.createdAt).toLocaleString()}</div>
              </div>
            ))}
            <form className="bt-comment-form" onSubmit={handleComment}>
              <textarea rows={3} placeholder="Add a comment..." value={commentBody} onChange={e => setCommentBody(e.target.value)} />
              <button className="btn btn-primary btn-sm" type="submit">Post comment</button>
            </form>
          </div>

          {ticket.notifySupplierLog?.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="form-section-title">Supplier notification log</div>
              {ticket.notifySupplierLog.map((n, i) => (
                <div key={i} className="bt-comment">
                  <div className="bt-comment-author">{n.triggeredBy?.name || 'Coordinator'} · {n.status}</div>
                  <div className="bt-comment-body">{n.message}</div>
                  <div className="bt-comment-time">{new Date(n.triggeredAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bt-side">
          <div className="card">
            <div className="form-section-title">Details</div>
            <div className="detail-grid">
              <div className="detail-row"><span>Supplier</span><span>{ticket.supplier ? `${ticket.supplier.name} (${ticket.supplier.vendorCode})` : '—'}</span></div>
              <div className="detail-row"><span>Reported by</span><span>{ticket.reportedByName || ticket.reportedByEmail || '—'}</span></div>
              <div className="detail-row"><span>Assigned to</span><span>{ticket.assignedTo?.name || 'Unassigned'}</span></div>
              <div className="detail-row"><span>Redirected to</span><span>{ticket.redirectedTo?.name || '—'}</span></div>
            </div>
          </div>

          {isBusinessLead && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="form-section-title">Coordinator actions</div>

              <div className="form-group">
                <label>Status</label>
                <select value={ticket.status} disabled={busy} onChange={e => patch({ status: e.target.value })}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Redirect to department</label>
                <select value={ticket.redirectedTo?._id || ''} disabled={busy} onChange={e => patch({ redirectedTo: e.target.value || null })}>
                  <option value="">Not redirected</option>
                  {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                </select>
              </div>

              {!ticket.assignedTo && (
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => patch({ assignedTo: user._id })}>
                  Assign to me
                </button>
              )}

              {redirectLink && (
                <div className="bt-redirect-link">
                  <span>Redirect link</span>
                  <div className="key-display">
                    <code>{redirectLink}</code>
                    <button className="icon-btn-sm" onClick={() => { navigator.clipboard.writeText(redirectLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                      {copied ? <CheckCircle size={12} color="var(--success)" /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
              )}

              {ticket.supplier?.contactEmail && (
                <div className="bt-notify">
                  {!showNotify ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowNotify(true)}><Send size={12} /> Notify Supplier</button>
                  ) : (
                    <form onSubmit={handleNotify} className="bt-notify-form">
                      <textarea rows={3} placeholder={`Message to ${ticket.supplier.contactEmail}...`} value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary btn-sm" type="submit">Send</button>
                        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowNotify(false)}>Cancel</button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
